<script setup lang="ts">
/**
 * Proveedores del Estado sancionados por Defensa del Consumidor.
 *
 * Reads the precomputed `udeco_supplier_stats` cross-reference. Nothing is joined on the request
 * path — see the endpoint for why the RUT match needs a 12-digit normalisation.
 *
 * FRAMING, which this page must never soften: a UDECO sanction is about how the firm treated
 * CONSUMERS. It says nothing about whether any public contract was irregular, and this page must
 * not be read as alleging that. What it shows is narrower and entirely factual — the State's own
 * consumer agency fined this firm, and the State keeps buying from it.
 */
import type { DataColumn } from '~/components/DataTable.vue'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const NuxtLinkComponent = resolveComponent('NuxtLink')

// The primary column must be declared first: `<DataTable>` always renders it as
// the first cell, so a later declaration misaligns the desktop header.
const actCols = computed<DataColumn[]>(() => [
  { key: 'fecha', label: t('sanciones.col.fecha'), primary: true, mono: true, width: '9rem' },
  { key: 'tipo', label: t('sanciones.col.tipo'), width: '9rem' },
  { key: 'motivo', label: t('sanciones.col.motivo') },
  { key: 'montoUr', label: t('sanciones.col.monto'), align: 'end', mono: true, width: '7rem' },
])

const sort = ref((route.query.sort as string) ?? 'fines')
const search = ref((route.query.search as string) ?? '')
const onlyFines = ref(route.query.onlyFines === 'true')
const page = ref(Number(route.query.page ?? 1))
const ITEMS_PER_PAGE = 25

watch([sort, search, onlyFines], () => {
  page.value = 1
})
watch([sort, search, onlyFines, page], () => {
  const q: Record<string, string> = {}
  if (sort.value !== 'fines') q.sort = sort.value
  if (search.value) q.search = search.value
  if (onlyFines.value) q.onlyFines = 'true'
  if (page.value > 1) q.page = String(page.value)
  router.replace({ query: q })
})

const { data: res, pending, error } = await useFetch<any>('/api/analytics/sanciones', {
  query: computed(() => ({
    page: page.value,
    limit: ITEMS_PER_PAGE,
    sortBy: sort.value,
    ...(search.value.trim().length >= 3 ? { search: search.value.trim() } : {}),
    ...(onlyFines.value ? { onlyFines: 'true' } : {}),
  })),
})

const firms = computed<any[]>(() => res.value?.data?.firms ?? [])
const meta = computed<any>(() => res.value?.data?.meta ?? null)
const totalPages = computed<number>(() => res.value?.data?.pagination?.totalPages ?? 1)

const SORT_ITEMS = computed(() => [
  { value: 'spend', title: t('sanciones.sort.spend') },
  { value: 'sanctions', title: t('sanciones.sort.sanctions') },
  { value: 'fines', title: t('sanciones.sort.fines') },
  { value: 'recent', title: t('sanciones.sort.recent') },
])

function formatDate(value?: string | Date | null): string {
  if (!value) return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`
}

/** The firm's contracts in the explorer, by the name the procurement corpus uses. */
function contractsLink(firm: any): string | null {
  const name = firm?.supplierName
  return name ? localePath(`/contracts?suppliers=${encodeURIComponent(name)}`) : null
}

useSeo(() => ({
  title: t('seo.sanciones.title'),
  description: t('seo.sanciones.description'),
  path: '/analytics/sanciones',
  kicker: 'Análisis',
}))
</script>

<template>
  <div class="san">
    <v-sheet
      class="hero"
      tag="header"
    >
      <div class="u-container">
        <div class="hero__in">
          <p class="hero__eyebrow u-mono">
            {{ t('home.eyebrow') }}
          </p>
          <h1 class="hero__title">
            {{ t('sanciones.title') }}
          </h1>
          <p class="hero__dek">
            {{ t('sanciones.lead') }}
          </p>
        </div>
      </div>
    </v-sheet>

    <div class="u-container page">
      <!-- The contract under which these numbers are published. Stays above the list. -->
      <v-alert
        type="info"
        variant="tonal"
        density="comfortable"
        class="caveat"
      >
        {{ t('sanciones.caveat') }}
      </v-alert>

      <div
        v-if="meta"
        class="kpis"
      >
        <div class="kpi">
          <span class="kpi__n u-mono">{{ meta.sellingToState }}</span>
          <span class="kpi__l">{{ t('sanciones.kpi.firms', { total: meta.sanctionedFirmsTotal }) }}</span>
        </div>
        <div class="kpi">
          <MoneyAmount
            :amount="meta.totalStateUyu"
            currency="UYU"
            compact
          />
          <span class="kpi__l">{{ t('sanciones.kpi.spend') }}</span>
        </div>
        <div class="kpi">
          <span class="kpi__n u-mono">{{ meta.totalSanctions }}</span>
          <span class="kpi__l">{{ t('sanciones.kpi.sanctions') }}</span>
        </div>
        <div class="kpi">
          <span class="kpi__n u-mono">{{ Math.round(meta.totalUr) }} UR</span>
          <span class="kpi__l">{{ t('sanciones.kpi.fines') }}</span>
        </div>
      </div>

      <div class="filters">
        <v-select
          v-model="sort"
          :items="SORT_ITEMS"
          :label="t('sanciones.sortLabel')"
          density="comfortable"
          variant="outlined"
          hide-details
        />
        <v-text-field
          v-model="search"
          :label="t('sanciones.searchLabel')"
          density="comfortable"
          variant="outlined"
          clearable
          hide-details
        />
        <v-checkbox
          v-model="onlyFines"
          :label="t('sanciones.onlyFines')"
          density="comfortable"
          hide-details
        />
      </div>

      <v-alert
        v-if="error"
        type="warning"
        variant="tonal"
      >
        {{ t('sanciones.notComputed') }}
      </v-alert>

      <v-progress-linear
        v-else-if="pending"
        indeterminate
        color="accent"
      />

      <ul
        v-else
        id="sanciones-firms"
        class="firms"
      >
        <li
          v-for="f in firms"
          :key="f.rut"
          class="firms__row"
        >
          <div class="firms__head">
            <component
              :is="contractsLink(f) ? NuxtLinkComponent : 'span'"
              :to="contractsLink(f) ?? undefined"
              class="firms__name"
            >
              {{ f.supplierName ?? f.razonSocial }}
            </component>
            <span
              v-if="f.nombreComercial && f.nombreComercial !== f.razonSocial"
              class="firms__alias"
            >{{ f.nombreComercial }}</span>
            <span class="firms__rut u-mono">{{ f.rut }}</span>
            <MoneyAmount
              :amount="f.totalUyu"
              currency="UYU"
              compact
              size="sm"
            />
          </div>

          <p class="firms__meta u-mono">
            {{ t('sanciones.row.contracts', { n: f.contracts, buyers: f.buyers }) }}
            · {{ t('sanciones.row.sanctions', { n: f.sanctions, ur: Math.round(f.totalUr) }) }}
            · {{ t('sanciones.row.last', { date: formatDate(f.lastSanctionAt) }) }}
          </p>

          <!-- The sanctions themselves. The counts above say how many; this says which — when,
               what kind, for what and how much. A reader checking the claim needs the acts, not
               the aggregate. Collapsed by default so a firm with 18 does not bury the next row. -->
          <details
            v-if="f.detail?.length"
            class="acts"
          >
            <summary class="acts__toggle">
              <!-- Spanish needs the singular: "las 1 sanciones" reads as broken copy. -->
              {{ f.detail.length === 1
                ? t('sanciones.detailToggleOne')
                : t('sanciones.detailToggle', { n: f.detail.length }) }}
            </summary>
            <!-- `<DataTable>`, not a raw table: four columns of act detail only
                 fit a phone as one card per act. -->
            <DataTable
              class="acts__table"
              :columns="actCols"
              :rows="f.detail"
              :row-key="(_s: any, i: number) => `${f.rut}-${i}`"
              min-width="520px"
            >
              <template #cell:fecha="{ row }">
                {{ formatDate(row.fecha) }}
              </template>

              <template #cell:tipo="{ row }">
                <!-- A fine and a caution are different acts; naming the type is the whole
                     point of listing them separately. -->
                <span
                  class="acts__tipo"
                  :class="{ 'acts__tipo--multa': row.tipo === 'Multa' }"
                >{{ row.tipo ?? '—' }}</span>
              </template>

              <template #cell:motivo="{ row }">
                {{ row.motivo ?? '—' }}
              </template>

              <template #cell:montoUr="{ row }">
                {{ row.montoUr > 0 ? `${Math.round(row.montoUr)} UR` : '—' }}
              </template>
            </DataTable>
          </details>

          <!-- Fallback for a firm whose individual acts did not load: the motives are still real. -->
          <ul
            v-else-if="f.motivos?.length"
            class="motivos"
          >
            <li
              v-for="m in f.motivos.slice(0, 6)"
              :key="m"
              class="motivos__item"
            >
              {{ m }}
            </li>
          </ul>
        </li>
      </ul>

      <!-- `<DataPager>`, not `<v-pagination>`: seven 48px number buttons plus
           prev/next need 432px and push the page sideways on a phone. -->
      <DataPager
        v-if="totalPages > 1"
        v-model:page="page"
        :total-pages="totalPages"
        scroll-target-id="sanciones-firms"
        class="pager"
      />

      <p
        v-if="meta"
        class="source u-mono"
      >
        {{ t('sanciones.source') }}
        <a
          :href="meta.sourceUrl"
          target="_blank"
          rel="noopener"
        >catalogodatos.gub.uy</a>
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* The spacing scale stops at `--s-9`; `--s-10` made the whole declaration
   invalid, so the hero shipped with no padding at all. */
.hero { background: var(--ink); color: var(--paper); padding-block: var(--s-7) var(--s-6); }
.hero__in { max-width: 46rem; }
.hero__eyebrow { font-size: var(--t-xs); opacity: 0.7; letter-spacing: 0.08em; text-transform: uppercase; }
.hero__title { font-family: var(--font-display); margin: var(--s-2) 0 var(--s-3); }
.hero__dek { opacity: 0.85; }

/* `padding-block`, never the `padding` shorthand — this element also carries
   `.u-container`, and a shorthand here wipes the site gutter on mobile. */
.page { padding-block: var(--s-8) var(--s-9); }
.caveat { margin-bottom: var(--s-5); }

.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
  gap: var(--s-3);
  margin-bottom: var(--s-6);
}

.kpi { border: 1px solid var(--rule); border-radius: var(--r-lg); background: var(--surface); padding: var(--s-4); }
.kpi__n { display: block; font-size: var(--t-xl); font-weight: 700; }
.kpi__l { display: block; font-size: var(--t-xs); color: var(--text-muted); }

.filters { display: flex; flex-wrap: wrap; align-items: center; gap: var(--s-3); margin-bottom: var(--s-4); }
.filters > :first-child { min-width: 14rem; }
.filters > :nth-child(2) { flex: 1 1 18rem; }

.firms { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--s-3); }

.firms__row {
  border: 1px solid var(--rule);
  border-radius: var(--r-lg);
  background: var(--surface);
  padding: var(--s-4);
}

.firms__head { display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--s-3); }
.firms__name { font-weight: 600; color: inherit; text-decoration: none; }
a.firms__name:hover { text-decoration: underline; }
.firms__alias { font-size: var(--t-sm); color: var(--text-muted); }
.firms__rut { font-size: var(--t-xs); color: var(--text-muted); }
.firms__meta { margin: var(--s-2) 0 0; font-size: var(--t-xs); color: var(--text-muted); }

.motivos { list-style: none; margin: var(--s-2) 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: var(--s-2); }

/* ---- The individual sanctions ---- */
.acts { margin-top: var(--s-3); }

.acts__toggle {
  cursor: pointer;
  font-size: var(--t-sm);
  color: var(--celeste-deep);
}

/* `<DataTable>` owns the frame, the scroll box and the card reflow. */
.acts__table { margin-top: var(--s-2); }

/* A fine is marked by the WORD plus weight — never by colour alone. */
.acts__tipo { color: var(--text-muted); }
.acts__tipo--multa { color: var(--alerta); font-weight: 600; }

.motivos__item {
  border: 1px solid var(--rule);
  border-radius: var(--r-sm);
  padding: 2px var(--s-2);
  font-size: var(--t-xs);
  color: var(--text-muted);
}

.pager { margin-top: var(--s-6); }
.source { margin-top: var(--s-5); font-size: var(--t-xs); color: var(--text-muted); }
</style>
